import React, { useEffect, useReducer, useState } from "react"
import FilterContext from "./filterContext"
import filterReducer from "./filterReducer"
import prismicService from "../utils/prismicService"
import { kebabCase } from "../utils/kebabCase"

const BlogState = (props) => {
	const initialState = {
		articles: [],
		filtered: [],
		searchResults: [],
		articlesToShow: [],
		isFiltered: false,
		searchQuery: "",
		paginationVal: false,
		filterCat: "all",
	}

	const [state, dispatch] = useReducer(filterReducer, initialState)
	const [sortedResults, setSortedResults] = useState([])
	const [paginatedData, setPaginatedData] = useState([])
	const [currentPage, setCurrentPage] = useState(1)
	const [loading, setLoading] = useState(false)
	const [query, setQuery] = useState()

	// Used to set the number of items to be displayed on the paginated page
	const PAGE_SIZE = 12

	// Total numbers of articles in the array
	const totalArticles = state.articlesToShow.length

	useEffect(() => {
		const fetchData = async () => {
			setLoading(true)
			// Fetch Articles Using Prismic Client Library
			const response = await prismicService.getAllArticles()
			setLoading(false)

			if (response) {
				dispatch({ type: "UPDATE", payload: response.results })
			}
		}
		fetchData()
	}, [])

	// Filter Articles
	const filterArticles = (text, attributeName) => {
		dispatch({ type: "FILTER_ARTICLES", payload: { text, attributeName } })
	}

	// Clear Filter
	const clearFilter = () => {
		dispatch({ type: "CLEAR_FILTER" })
	}

	const clearSearch = () => {
		dispatch({ type: "CLEAR_SEARCH" })
	}

	const setPaginationValue = (pageVal) => {
		dispatch({ type: "SET_PAGINATION", payload: pageVal })
	}

	const setFilterCategory = (filterCategory) => {
		dispatch({ type: "SET_FILTER_CATEGORY", payload: filterCategory })
	}

	const setSearchQuery = (query) => {
		dispatch({ type: "SET_SEARCH_QUERY", payload: query })
	}

	const setArticlesToShow = (articles) => {
		dispatch({ type: "SET_ARTICLES_TO_SHOW", payload: articles })
	}

	//Sort the articles by published date entered in prismic
	const sortArticles = (articles) => {
		if (!articles.length) return []

		const sortedData = [...articles].sort(
			(a, b) => new Date(a.data.published_date) - new Date(b.data.published_date)
		)

		return sortedData.reverse()
	}

	useEffect(() => {
		if (!state.articlesToShow.length) return

		const data = sortArticles(state.articlesToShow)

		setSortedResults(data)
		setCurrentPage(1)
	}, [state.articlesToShow])

	useEffect(() => {
		if (!sortedResults.length) return

		const firstPageIndex = (currentPage - 1) * PAGE_SIZE
		const lastPageIndex = firstPageIndex + PAGE_SIZE
		const data = sortedResults.slice(firstPageIndex, lastPageIndex)

		setPaginatedData(data)
	}, [sortedResults, currentPage, PAGE_SIZE])

	useEffect(() => {
		if (!state.searchQuery) {
			clearSearch()
			filterArticles(state.filterCat, "category")

			return
		}
		filterArticles(state.searchQuery, "title")
	}, [state.searchQuery])

	useEffect(() => {
		filterArticles(state.filterCat, "category")
		if (state.searchQuery) {
			filterArticles(state.searchQuery, "title")
		}
	}, [state.filterCat])

	useEffect(() => {
		if (state.searchResults.length) {
			setArticlesToShow(state.searchResults)
			return
		}

		if (state.filtered.length && query !== "all") {
			setArticlesToShow(state.filtered)
		} else {
			setArticlesToShow(state.articles)
		}
	}, [
		state.articles,
		state.filtered,
		state.searchResults,
		currentPage,
		state.paginationVal,
		query,
	])

	// Handle Triggering of filtering logic when the user is navigating the blog using the browser history or direct URLs
	useEffect(() => {
		if (query !== "all" && query?.length) {
			filterArticles(kebabCase(query), "category")
		}
	}, [query, state.articles])

	useEffect(() => {
		setQuery(query)
		setFilterCategory(query)
	}, [query])

	useEffect(() => {}, [state.filterCat])

	return (
		<FilterContext.Provider
			value={{
				articles: state.articles,
				filtered: state.filtered,
				searchResults: state.searchResults,
				articlesToShow: state.articlesToShow,
				setArticlesToShow,
				filterArticles,
				clearFilter,
				clearSearch,
				setPaginationValue,
				isFiltered: state.isFiltered,
				paginationVal: state.paginationVal,
				filterCat: state.filterCat,
				setFilterCategory,
				setSearchQuery,
				currentPage,
				sortedResults,
				paginatedData,
				setQuery,
				totalArticles,
				setCurrentPage,
				PAGE_SIZE,
				loading,
			}}
		>
			{props.children}
		</FilterContext.Provider>
	)
}

export default BlogState
